/**
 * Beglaubigung der Dateien, die die App schreibt.
 *
 * Zweck: nachweisen, dass eine Datei seit ihrer Entstehung **unverändert** ist und von
 * **diesem Gerät** stammt. Nicht mehr — und das ist wichtig zu wissen: solange der
 * öffentliche Schlüssel nirgends hinterlegt ist, heisst eine gültige Signatur nur
 * „derselbe Schlüssel wie beim letzten Mal". Zum Echtheitsnachweis wird sie erst, wenn der
 * Server den Schlüssel dieses Geräts kennt (Phase 4, s. `docs/beglaubigung.md`).
 *
 * ## Wo die Beglaubigung liegt
 *
 * In einer **Begleitdatei** neben der Datei: gleicher Name, Endung `.sig`. Aus
 * `foto.png` wird `foto.png.sig`.
 *
 * Zuvor wurde sie an die Datei **angehängt**; gemessen am 2026-09-10 tragen PNG, JPEG, M4A
 * und MP4 das klaglos, weil sie eine Abschlussmarke haben. **SVG nicht** — XML kennt kein
 * „danach", und die Handschrift brauchte deshalb einen Sonderweg über `<metadata>`. Mit der
 * Begleitdatei fällt dieser Sonderfall weg: ein Verfahren für alle Formate, ein Prüfweg,
 * und der Server kann `.sig` neben die Datei legen und ohne Formatwissen prüfen.
 *
 * **Was man dabei aufgibt:** die Datei allein trägt ihre Beglaubigung nicht mehr, beide
 * können getrennt werden. Abgefangen dadurch, dass das Manifest **Dateiname und Hash**
 * nennt — eine verwaiste oder vertauschte Begleitdatei fällt beim Prüfen auf.
 */

export interface Manifest {
  /** SHA-256 des Inhalts **ohne** den angehängten Block, in Kleinbuchstaben-Hex. */
  sha256: string;
  /** Dateiname ohne Pfad. Eine abgetrennte Beglaubigung fällt dadurch auf. */
  fileName: string;
  /**
   * Relay-Zeitform `yyyyMMddHHmmssSSS` — **Ortszeit des Geräts, ohne Zone** (so führt Relay
   * alle Zeiten). Allein ist sie mehrdeutig, deshalb steht die Abweichung von UTC daneben.
   */
  createdAt: string;
  /**
   * Abweichung von UTC zum Zeitpunkt der Beglaubigung, z. B. `+02:00`.
   *
   * ⚠️ Ohne dieses Feld wäre `createdAt` als Beleg wertlos: „10:05" in welcher Zone? Zwischen
   * Sommer- und Winterzeit liegt eine Stunde, und ein Prüfungsgerät kann auch in einer
   * anderen Zone stehen. Relay lässt die Zone in `char(17)` nicht zu — sie gehört deshalb als
   * eigenes Feld ins Manifest und nicht in die Zeit.
   */
  createdAtOffset: string;
  /**
   * Woher die Zeit stammt.
   *
   * - `system` — nur die Uhr des Geräts. Sie kann beliebig falsch gehen; niemand hat
   *   nachgesehen.
   * - `system-ntp-checked` — dieselbe Uhr, aber sie wurde gegen die Netzzeit geprüft.
   *   `ntpOffsetSeconds` und `ntpCheckedAt` sagen, wie weit sie daneben lag und wann.
   *
   * ⚠️ Ohne dieses Feld wäre nicht zu unterscheiden, ob eine Zeit belastbar ist oder bloss
   * behauptet. Und auch `system-ntp-checked` ist **kein Beweis**: SNTP ist unsigniert (siehe
   * `docs/beglaubigung.md`).
   */
  timeSource: 'system' | 'system-ntp-checked';
  /** Abweichung bei der letzten Prüfung, Sekunden mit einer Nachkommastelle. Leer ohne Prüfung. */
  ntpOffsetSeconds: string;
  /** Wann geprüft wurde, Relay-Zeitform. Leer ohne Prüfung. */
  ntpCheckedAt: string;
  /** Gegen wen geprüft wurde. Leer ohne Prüfung. */
  ntpServer: string;
  /** Die selbst vergebene, stabile Kennung dieses Geräts. */
  deviceId: string;
  /** Der vom Benutzer vergebene Name, z. B. „Prüfungs-iPad 3". */
  deviceName: string;
  /** Modellkürzel, z. B. `iPad7,5`. */
  deviceModel: string;
  osVersion: string;
  /**
   * `identifierForVendor`.
   *
   * ⚠️ **Zweitrangig und nicht stabil**: sie wechselt, sobald die letzte App desselben
   * Anbieters entfernt wird. Sie steht hier als zusätzlicher Anhaltspunkt — zwei Dateien
   * mit gleicher `deviceId`, aber verschiedener `vendorId` sind ein Hinweis darauf, dass die
   * App zwischenzeitlich neu installiert wurde. Als Kennung taugt sie nicht.
   */
  vendorId: string;
  appVersion: string;
  examId: string;
  /**
   * Die Station — der **Ort** im Sinne der Prüfung (Raum B 2.09 und so fort steht daran).
   * Leer, wenn die Datei zu keiner Station gehört.
   */
  stationId: string;
  candidateId: string;
  examinerId: string;
  /**
   * Woher der Schlüssel kommt. `secure-enclave` heisst: der private Schlüssel liegt in
   * Hardware und ist auch mit Gerätezugriff nicht auslesbar. `keychain` ist der Rückfall
   * (Simulator, ältere Geräte) und trägt deutlich weniger. Das gehört **ins Manifest**, damit
   * niemand später einer Simulator-Signatur dieselbe Beweiskraft zuspricht wie einer echten.
   *
   * ⚠️ **`web-crypto` seit dem 2026-09-22** (`platform/signing.ts`, Web-Rückfall). Der
   * Schlüssel ist aus der Seite heraus nicht auslesbar (`extractable: false`), liegt aber im
   * **Browserprofil** — ohne Hardware, ohne Apple-Bescheinigung. Er trägt am wenigsten von
   * allen dreien, die etwas tragen; die Rechnung dahinter ist dieselbe (ECDSA P-256,
   * SHA-256, IEEE P1363). Die Tabelle im Langen steht in `docs/beglaubigung.md`.
   *
   * ⚠️ **`safe-storage` seit dem 2026-09-22** (Desktop, `desktop/schluessel.cts`). Der private
   * Teil liegt mit `safeStorage` verschlüsselt auf der Platte — DPAPI auf Windows, Schlüsselbund
   * auf macOS — und **verlässt den Hauptprozess nicht**: das Fenster bekommt nur den
   * öffentlichen Punkt und fertige Unterschriften. Das trägt mehr als `web-crypto` (kein
   * Browserprofil, kein Signieren im Renderer) und weniger als `secure-enclave` (keine
   * Hardware, keine Bescheinigung). Steht `safeStorage` nicht bereit — Linux ohne
   * Schlüsselbund —, bleibt es bei `web-crypto`; einen Schlüssel im Klartext gibt es nicht.
   */
  keyKind: 'secure-enclave' | 'keychain' | 'ephemeral' | 'web-crypto' | 'safe-storage';
}

/**
 * Das Manifest in genau einer Schreibweise.
 *
 * ⚠️ Die Reihenfolge der Felder steht **hier** und nicht bei `JSON.stringify`. Signiert wird
 * eine Zeichenkette; wäre ihre Erzeugung von der Einfügereihenfolge eines Objekts abhängig,
 * ergäbe dieselbe Aussage je nach Aufrufweg eine andere Signatur — und die Prüfung schlüge
 * fehl, ohne dass jemand etwas verändert hätte.
 */
export function canonicalManifest(m: Manifest): string {
  const felder: [keyof Manifest, string][] = [
    ['sha256', m.sha256],
    ['fileName', m.fileName],
    ['createdAt', m.createdAt],
    ['createdAtOffset', m.createdAtOffset],
    ['timeSource', m.timeSource],
    ['ntpOffsetSeconds', m.ntpOffsetSeconds],
    ['ntpCheckedAt', m.ntpCheckedAt],
    ['ntpServer', m.ntpServer],
    ['deviceId', m.deviceId],
    ['deviceName', m.deviceName],
    ['deviceModel', m.deviceModel],
    ['osVersion', m.osVersion],
    ['vendorId', m.vendorId],
    ['appVersion', m.appVersion],
    ['examId', m.examId],
    ['stationId', m.stationId],
    ['candidateId', m.candidateId],
    ['examinerId', m.examinerId],
    ['keyKind', m.keyKind],
  ];
  return '{' + felder.map(([k, v]) => JSON.stringify(k) + ':' + JSON.stringify(v)).join(',') + '}';
}

/** Was in der `.sig`-Datei steht. */
export interface SignatureFile {
  /** Fassung des Formats. Steigt, wenn sich Felder ändern — der Server prüft sie zuerst. */
  version: 1;
  algorithm: 'ECDSA-P256-SHA256';
  /**
   * Wie die Unterschrift kodiert ist.
   *
   * ⚠️ Steht ausdrücklich dabei, weil genau hier eine Java-Prüfung scheitert: CryptoKit
   * liefert `r||s` (IEEE P1363), Javas `SHA256withECDSA` erwartet dagegen DER. Wer das Feld
   * liest, weiss, dass er `SHA256withECDSAinP1363Format` braucht.
   */
  signatureFormat: 'IEEE-P1363';
  /** SHA-256 des öffentlichen Schlüssels, hex. Damit findet der Server den hinterlegten. */
  keyId: string;
  /** Der öffentliche Schlüssel, roh `x||y` in Base64 — **ohne** das Präfix `0x04`. */
  publicKey: string;
  /**
   * Die signierte Zeichenkette, **wörtlich**.
   *
   * ⚠️ Nicht das Manifest als Objekt: geprüft wird über genau diese Bytes. Ein Server, der
   * das Objekt neu serialisiert, erzeugt womöglich eine andere Reihenfolge oder andere
   * Leerzeichen — und eine gültige Unterschrift schlüge fehl. Die Felder liest man, indem
   * man diese Zeichenkette parst.
   */
  signedManifest: string;
  signature: string;
}

/** Der Inhalt der Begleitdatei. */
export function signatureFile(
  m: Manifest,
  signature: string,
  publicKey: string,
  keyId: string,
): string {
  const inhalt: SignatureFile = {
    version: 1,
    algorithm: 'ECDSA-P256-SHA256',
    signatureFormat: 'IEEE-P1363',
    keyId,
    publicKey,
    signedManifest: canonicalManifest(m),
    signature,
  };
  return JSON.stringify(inhalt, null, 2) + '\n';
}

/** Wie die Begleitdatei zu einer Datei heisst. */
export const sigPfad = (pfad: string) => pfad + '.sig';
